import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy } from '@phosphor-icons/react';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board } from '@/chess/board';
import { FixedNav, Caption, Card, Button, PulseDot } from '@/ui';
import { practiceApi } from './api';
import { BadgeUnlockedModal } from './BadgeUnlockedModal';
import { CADENCE_TIMING } from './types';
import type { BadgeListItem, PracticeGameDetail, PracticeResult } from './types';

/**
 * Practice Review page — post-partie (Salve P1 T5).
 *
 * Affiche le board navigable coup par coup + stats + boutons rejouer/retour.
 * Le board est REPLAY-ONLY : pas d'interactivité, position figée à un ply
 * sélectionné via slider/boutons prev/next.
 *
 * XP gagné est lu via le hook XP existant (rafraîchi après le finish T4).
 * BadgeUnlockedModal sera connectée en T7 (badges seed). T5 affiche une zone
 * placeholder vide pour l'instant — non-bloquant.
 */

const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const RESULT_LABEL: Record<PracticeResult, string> = {
  win: 'Victoire',
  loss: 'Défaite',
  draw: 'Nulle',
  abandoned: 'Abandon',
};

const RESULT_TONE: Record<PracticeResult, { text: string; bg: string }> = {
  win: { text: 'text-volt-500', bg: 'bg-volt-500/10' },
  loss: { text: 'text-coral-500', bg: 'bg-coral-500/10' },
  draw: { text: 'text-ink-300', bg: 'bg-ink-300/10' },
  abandoned: { text: 'text-amber-500', bg: 'bg-amber-500/10' },
};

export function PracticeReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const gameId = id ? Number(id) : NaN;
  const navigate = useNavigate();

  const gameQ = useQuery({
    queryKey: ['practice', 'game', gameId],
    queryFn: () => practiceApi.findGame(gameId),
    enabled: !Number.isNaN(gameId),
  });

  if (Number.isNaN(gameId)) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-coral-500" data-testid="invalid-id">ID de partie invalide.</p>
        </main>
      </div>
    );
  }

  if (gameQ.isLoading) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-ink-300" data-testid="loading">Chargement de la review…</p>
        </main>
      </div>
    );
  }

  if (gameQ.isError || !gameQ.data) {
    return (
      <div className="min-h-screen bg-ink-900 text-paper-50">
        <FixedNav />
        <main className="pt-20 px-4 max-w-3xl mx-auto">
          <p className="text-coral-500" data-testid="load-error">Impossible de charger la review.</p>
        </main>
      </div>
    );
  }

  return (
    <ReviewContent
      game={gameQ.data}
      onBack={() => navigate('/practice-arena')}
      onReplay={() => navigate('/practice-arena/setup')}
      onGotoBadges={() => navigate('/practice-arena/badges')}
    />
  );
}

/**
 * Forme attendue de `useLocation().state` quand l'utilisateur arrive depuis
 * GamePage post-finish (M4 P2). Si `null` (arrivée depuis ListPage par
 * exemple), la review n'affiche ni la carte récompenses ni le modal badges.
 */
interface ReviewNavState {
  xpAwarded?: boolean;
  xpAmount?: number;
  unlockedBadges?: string[];
}

function ReviewContent({
  game,
  onBack,
  onReplay,
  onGotoBadges,
}: {
  game: PracticeGameDetail;
  onBack: () => void;
  onReplay: () => void;
  onGotoBadges: () => void;
}): JSX.Element {
  // Position calculée selon le ply sélectionné dans le slider
  const [plyIndex, setPlyIndex] = useState<number>(game.moves.length);

  // M4 P2 — récupère le state passé par GamePage post-finish (xp + badges
  // débloqués). Null si arrivée depuis Liste (UX : pas de récompenses fantômes).
  const navState = (useLocation().state ?? null) as ReviewNavState | null;
  const xpAmount = navState?.xpAmount ?? 0;
  const xpAwarded = navState?.xpAwarded === true;
  const unlockedBadgeSlugs = navState?.unlockedBadges ?? [];
  const hasRewards = xpAwarded && xpAmount > 0 || unlockedBadgeSlugs.length > 0;

  // H1 P2 — fetch les détails des badges débloqués pour le modal de célébration.
  // On déclenche `listBadges` uniquement si au moins un slug est passé via state.
  const [modalBadges, setModalBadges] = useState<BadgeListItem[] | null>(null);
  const [modalDismissed, setModalDismissed] = useState(false);
  useEffect(() => {
    if (unlockedBadgeSlugs.length === 0 || modalDismissed) return;
    let cancelled = false;
    practiceApi
      .listBadges()
      .then((resp) => {
        if (cancelled) return;
        const filtered = resp.badges.filter((b) => unlockedBadgeSlugs.includes(b.slug));
        if (filtered.length > 0) setModalBadges(filtered);
      })
      .catch((err: unknown) => {
        console.error('[practice-arena] listBadges failed for unlock modal', err);
      });
    return () => {
      cancelled = true;
    };
    // unlockedBadgeSlugs vient d'un nav state stable au mount — on n'inclut pas
    // sa référence pour éviter une boucle si React re-rend ReviewContent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDismissed]);

  const positions = useMemo(() => computePositions(game), [game]);
  const currentFen = positions[plyIndex] ?? (game.starting_fen ?? INITIAL_FEN);
  const lastMoveKeys = useMemo(() => {
    if (plyIndex === 0 || plyIndex > game.moves.length) return undefined;
    const m = game.moves[plyIndex - 1];
    if (!m) return undefined;
    const from = m.uci.slice(0, 2) as Key;
    const to = m.uci.slice(2, 4) as Key;
    return [from, to] as [Key, Key];
  }, [plyIndex, game.moves]);

  const result = (game.result ?? 'abandoned') as PracticeResult;
  const tone = RESULT_TONE[result];
  const durationLabel = formatDuration(game.duration_ms);
  const cadenceLabel = CADENCE_TIMING[game.cadence].label;

  return (
    <div className="min-h-screen bg-ink-900 text-paper-50">
      <FixedNav />
      <main className="pt-20 pb-12 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <PulseDot color="var(--orange-500)" />
          <Caption color="orange">Review</Caption>
        </div>
        <h1 className="text-heading-1 font-display font-semibold mb-6">
          Partie #{game.id} — Stockfish ELO {game.level_elo}
        </h1>

        {/* M4 P2 — Récompenses depuis nav state (XP + badges débloqués). */}
        {hasRewards && (
          <div className="mb-6" data-testid="rewards-card">
            <Card accent="orange">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <Caption color="orange">Récompenses</Caption>
                  {xpAwarded && xpAmount > 0 && (
                    <p
                      className="mt-1 text-display-2 font-display text-orange-500"
                      data-testid="xp-awarded"
                    >
                      +{xpAmount} XP
                    </p>
                  )}
                  {unlockedBadgeSlugs.length > 0 && (
                    <button
                      type="button"
                      onClick={onGotoBadges}
                      className="mt-2 inline-flex items-center gap-2 text-body-sm text-orange-500 hover:underline"
                      data-testid="badges-unlocked-link"
                    >
                      <Trophy size={18} weight="fill" aria-hidden />
                      {unlockedBadgeSlugs.length === 1
                        ? '1 badge débloqué — Voir la collection'
                        : `${unlockedBadgeSlugs.length} badges débloqués — Voir la collection`}
                    </button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div data-testid="result-card">
            <Card padding="compact" className={tone.bg}>
              <Caption>Résultat</Caption>
              <p className={`text-display-2 font-display ${tone.text}`}>
                {RESULT_LABEL[result]}
              </p>
            </Card>
          </div>
          <Card padding="compact">
            <Caption>Coups</Caption>
            <p className="text-display-2 font-display tabular-nums">{game.moves_count}</p>
          </Card>
          <Card padding="compact">
            <Caption>Durée</Caption>
            <p className="text-display-2 font-display tabular-nums">{durationLabel}</p>
          </Card>
          <Card padding="compact">
            <Caption>Cadence</Caption>
            <p className="text-body-lg font-medium mt-1">{cadenceLabel}</p>
          </Card>
        </div>

        {/* Board + nav coups */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          <div className="flex flex-col gap-3">
            <Board
              fen={currentFen}
              orientation={game.resolved_color}
              interactive={false}
              {...(lastMoveKeys ? { lastMove: lastMoveKeys } : {})}
            />
            <div className="flex items-center gap-2" data-testid="ply-controls">
              <Button
                variant="secondary"
                size="compact"
                onClick={() => setPlyIndex(0)}
                disabled={plyIndex === 0}
              >
                ⏮
              </Button>
              <Button
                variant="secondary"
                size="compact"
                onClick={() => setPlyIndex(Math.max(0, plyIndex - 1))}
                disabled={plyIndex === 0}
              >
                ◀
              </Button>
              <input
                type="range"
                min={0}
                max={game.moves.length}
                value={plyIndex}
                onChange={(e) => setPlyIndex(Number(e.target.value))}
                className="flex-1 accent-orange-500"
                aria-label="Position dans la partie"
                title="Position dans la partie"
                data-testid="ply-slider"
              />
              <Button
                variant="secondary"
                size="compact"
                onClick={() => setPlyIndex(Math.min(game.moves.length, plyIndex + 1))}
                disabled={plyIndex === game.moves.length}
              >
                ▶
              </Button>
              <Button
                variant="secondary"
                size="compact"
                onClick={() => setPlyIndex(game.moves.length)}
                disabled={plyIndex === game.moves.length}
              >
                ⏭
              </Button>
            </div>
            <p className="text-caption text-ink-300 tabular-nums text-center">
              Coup {plyIndex} / {game.moves.length}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {/* Historique complet */}
            <Card padding="compact">
              <Caption>Historique</Caption>
              <ol
                className="mt-2 text-body-sm font-mono space-y-0.5 max-h-96 overflow-y-auto"
                data-testid="full-history"
              >
                {game.moves.length === 0 && <li className="text-ink-300">—</li>}
                {game.moves.map((m, idx) => {
                  const isCurrent = idx === plyIndex - 1;
                  return (
                    <li
                      key={m.ply}
                      className={`px-1 cursor-pointer hover:bg-ink-700 rounded ${
                        isCurrent ? 'bg-orange-500/20 text-orange-500' : ''
                      }`}
                      onClick={() => setPlyIndex(idx + 1)}
                      data-testid={`history-ply-${m.ply}`}
                    >
                      {Math.ceil(m.ply / 2)}.{m.ply % 2 === 0 ? '..' : ''} {m.san}
                    </li>
                  );
                })}
              </ol>
            </Card>

            {/* Boutons d'action */}
            <Card padding="compact">
              <div className="flex flex-col gap-2">
                <Button variant="primary" accent="orange" onClick={onReplay}>
                  🔄 Rejouer une partie
                </Button>
                <Button variant="secondary" onClick={onBack}>
                  Retour à L'Arène
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* H1 P2 — Modal de célébration au mount si badges débloqués. */}
      {modalBadges && (
        <BadgeUnlockedModal
          badges={modalBadges}
          onClose={() => {
            setModalBadges(null);
            setModalDismissed(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * Pré-calcule toutes les positions FEN du game (1 par ply + position initiale).
 * Permet la navigation slider instantanée sans recompute à chaque tick.
 *
 * C1 P2 : la position initiale vient de `game.starting_fen` (DTO enrichi, mig
 * 019) si le thème en porte un, sinon startpos. Sans ça, les parties à thème
 * (ex. finale tour-pion) restartent visuellement depuis la position initiale
 * standard côté review — incohérent avec ce que Léon a joué.
 */
function computePositions(game: PracticeGameDetail): string[] {
  const start = game.starting_fen ?? INITIAL_FEN;
  const fens: string[] = [start];
  const chess = new Chess(start);
  for (const m of game.moves) {
    const from = m.uci.slice(0, 2);
    const to = m.uci.slice(2, 4);
    const promo = m.uci.length >= 5 ? m.uci[4] : undefined;
    try {
      chess.move({ from, to, ...(promo ? { promotion: promo } : {}) });
      fens.push(chess.fen());
    } catch {
      break;
    }
  }
  return fens;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
